/* eslint-disable react/jsx-props-no-spreading */
import GoToAppOrStore from '@/components/GoToAppOrStore';
import { PersonPostPutRequest } from '@/models/Person';
import ChatBotFloatingButton from '@/pages/Chat/ChatBotFloatingButton';
import { putPersonRequest } from '@/store/person/action';
import React, { useEffect, useState } from 'react';
import { isAndroid, isIOS, isMobileOnly } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, RouteComponentProps, useHistory, useLocation } from 'react-router-dom';
import { Dispatch } from 'redux';
import Constants from '../Constants';
import Footer from '../components/Footer/footer';
import Header from '../components/Header/header';
import Loader from '../components/Loader';
import ScormCDNTest from '../components/ScormCDNTest/scormCDNTest';
import { Globals } from '../models/Globals';
import OrganizationNotFound from '../pages/OrganizationNotFound/OrganizationNotFound';
import { FlowEvents, analyticsService } from '../services/AnalyticsService';
import { getAuth, onboardRequired, setLocale as setLocalSvc } from '../services/IdentityService';
import { getSelectedProfile } from '../services/ProfileService';
import { ApplicationState } from '../store';
import { getAppOrgRequest } from '../store/appOrganization/action';
import { getContentLocaleRequest, setLocale } from '../store/locale/action';
import en from '../translations/en';
import pt from '../translations/pt';

interface RouteWrapper {
    component: React.ComponentType<RouteComponentProps>;
    isPrivate?: boolean;
    isAdmin?: boolean;
    exact?: boolean;
    path?: string;
    strict?: boolean;
}

interface PerformanceEntryUI extends PerformanceEntry {
    type: string;
}

const mobileRoutes = ['/integration', '/recommendations/contents', '/lms/class'];

const RouteWrapper = ({ component: Component, isPrivate, isAdmin, exact, path, strict }: RouteWrapper) => {
    const [isMobile, setIsMobile] = useState<boolean>(false);
    const [pageReady, setPageReady] = useState(false);
    const dispatch: Dispatch = useDispatch();
    const history = useHistory();
    const isAuthenticated: boolean = !!getAuth();
    const { loading, organization, colors, hasError, person, user, showHeader, isWhiteLabel, isTotvs, availableContentLanguages } = useSelector(
        (state: ApplicationState) => ({
            loading: state.appOrganization.loading,
            organization: state.appOrganization.data.organization,
            colors: state.appOrganization.data.colors,
            hasError: state.appOrganization.hasError,
            person: state.identity.data.person,
            user: state.identity.data.user,
            showHeader: state.header.show,
            isWhiteLabel: state.appOrganization.data.isWhiteLabel,
            isTotvs: state.appOrganization.data.isTotvs,
            availableContentLanguages: state.locale.availableContentLanguages,
        }),
    );

    const location = useLocation();

    useEffect(() => {
        if (!pageReady) {
            analyticsService.initialize();
            const url: string = window.location.href.split('#')[0];
            dispatch(getAppOrgRequest(encodeURIComponent(url)));
        } else {
            if (person) {
                analyticsService.setUserPropertiesConfiguration(person);
                dispatch(setLocale(person.locale === 'en' ? en : pt));
                setLocalSvc(person.locale);
                if (availableContentLanguages.length === 0) {
                    dispatch(getContentLocaleRequest());
                }
            }

            setIsMobile(isMobileOnly && (isAndroid || isIOS));
        }
    }, [dispatch, pageReady, person, availableContentLanguages.length]);

    useEffect(() => {
        if (pageReady) {
            const isPageReload = performance
                .getEntriesByType('navigation')
                .some((p: PerformanceEntry) => (p as PerformanceEntryUI).type === 'reload');

            if (!isPageReload && person?.id) {
                analyticsService.logEvent(FlowEvents.NEW_ACCESS);
            }
        }
    }, [pageReady]);

    useEffect(() => {
        if (person && person.id) {
            const selectedProfile = person && person.dynamicAttributes && person.dynamicAttributes.selectedProfile;

            const pathIgnore: string[] = ['/', '/page-not-found'];

            if (!isAdmin && !pathIgnore.includes(history.location.pathname) && selectedProfile === 'HR') {
                const studentProfile = 'STUDENT';
                person.dynamicAttributes = person.dynamicAttributes
                    ? { ...person.dynamicAttributes, selectedProfile: studentProfile }
                    : { selectedProfile: studentProfile };

                dispatch(putPersonRequest({ person, history, willClose: false } as PersonPostPutRequest));
            }
        }
    }, [isAdmin, history.location.pathname]);

    useEffect(() => {
        analyticsService.setCurrentScreen(location.pathname);
    }, [location]);

    useEffect(() => {
        if (!loading && (organization || hasError)) {
            if (person && organization && person.orgId !== organization.id) {
                history.push('/logout');
            }

            const version: number = ((window as unknown) as Globals).__VERSION__;
            if (process.env.NODE_ENV === 'production' && !hasError) {
                const styleElement: HTMLLinkElement = document.createElement('link');
                styleElement.rel = 'stylesheet';
                styleElement.href = `/rest/style/?color1=${colors[0]}&version=${version}&appKey=${Constants.APP_KEY}`.replace(/#/g, '%23');

                const styleChild = document.head.appendChild(styleElement);
                styleChild.onload = () => {
                    setPageReady(true);
                };
            } else {
                setPageReady(true);
            }
        }
    }, [colors, hasError, loading, organization, person, history]);

    if (!pageReady) {
        return <Loader />;
    }

    if (hasError) {
        return <OrganizationNotFound />;
    }

    if (isMobile && !mobileRoutes.some((route) => history.location.pathname.includes(route))) {
        return <GoToAppOrStore autoRedirect />;
    }

    if (!isAuthenticated && isPrivate) {
        return <Redirect to="/login" />;
    }

    if (isAuthenticated && isPrivate && onboardRequired(user, person) && !isWhiteLabel && !isTotvs) {
        return <Redirect to="/onboardingIntroduction" />;
    }

    if (
        isAuthenticated &&
        !isPrivate &&
        history.location.pathname !== '/login' &&
        !onboardRequired(user, person) &&
        history.location.pathname !== '/onboardingAcknowledgement' &&
        history.location.pathname !== '/integration'
    ) {
        return <Redirect to="/" />;
    }

    if (isPrivate) {
        if (isAdmin && getSelectedProfile() !== 'HR') {
            return <Redirect to="/" />;
        }

        return (
            <>
                {showHeader && <Header />}
                <section>
                    <Route exact={exact} path={path} render={(props) => <Component {...props} />} strict={strict} />
                </section>
                <ScormCDNTest />
                <ChatBotFloatingButton />
                <Footer />
            </>
        );
    }

    return <Route exact={exact} path={path} render={(props) => <Component {...props} />} />;
};

export default RouteWrapper;
